import { filter, flatten, intersection, map, orderBy, sortBy, uniq, uniqBy } from 'lodash'
import { store } from 'store/reducers'
import { hashTable, getReportsToProjects, getReportsToTeams } from 'services/Utilities'

import { t } from 'translations'

type Filter = {
  positions: string[]
  departments: string[]
  projects: string[]
  projectCodes: string[]
  teams: string[]
  vacationTypeId: string[]
  showAllPositions: boolean
  reportsTo: string[]
}

export type HierarchisedPosition = {
  _id: string
  id?: string
  name: string
  code?: string
  index?: number
  parentId?: string
  expanded?: boolean
  nonAssignable?: boolean
  resourceType?: string
  children?: []
  parent?: string | undefined
  picture?: string | null
  firstName?: string
  lastName?: string
}

export const hierarchiseSchedule = (
  events: MData.Event[] = [],
  filter: Filter = { positions: [], departments: [], projects: [], projectCodes: [], teams: [], vacationTypeId: [], showAllPositions: true, reportsTo: [] },
  collapsedResources = {}
): HierarchisedPosition[] => {
  const {
    settings: { currentScheduleHierarchy, eventSortField },
    resources: { positionsById, projectsById, projects, teamsById, teams, scheduleTemplatesById, users }
  } = store.getState() as MStore.RootState

  const projectsByCode = hashTable(projects, 'code')

  const projectIds = filter.reportsTo?.length ? map(getReportsToProjects(filter.reportsTo[0]), '_id') : filter.projects
  const filteredTeams = filter.reportsTo?.length ? map(getReportsToTeams(filter.reportsTo[0]), '_id') : filter.teams
  // Filter the structure based on the current filter
  const filteredPositions = filterStructure({
    positionsToFilter: filter.positions,
    departments: filter.departments,
    projectIds,
    projectCodes: filter.projectCodes,
    teams: filteredTeams,
    reportsTo: !!filter.reportsTo?.length,
    vacationTypeId: filter.vacationTypeId,
    showAllPositions: filter.showAllPositions,
    events
  })

  let positionResources: HierarchisedPosition[] = []

  switch (currentScheduleHierarchy) {
    case 'projects': {
      // Used to hide positions based on filter type in project view
      const filteredPositionIds = filteredPositions.map(({ _id }) => _id)

      // Get a list of all projects and their positions for the day
      const positionsPerProject = {}
      for (const event of events) {
        const [eventProject] = event.projects || []
        if (!eventProject) {
          if (!positionsPerProject['noProject']) positionsPerProject['noProject'] = new Set()
          positionsPerProject['noProject'].add(event.positionId)
        } else {
          if (!positionsPerProject[eventProject]) positionsPerProject[eventProject] = new Set()
          positionsPerProject[eventProject].add(event.positionId)

          // Adds the required positions of the template
          const shiftTemplate = event.templateId && scheduleTemplatesById[event.templateId]
          if (shiftTemplate && shiftTemplate.requiredPositions) {
            shiftTemplate.requiredPositions.forEach(positionId => positionsPerProject[eventProject].add(positionId))
          }

          // Adds the required positions of the project
          const shiftProject = projectsById[eventProject]
          if (shiftProject && shiftProject.positions) {
            shiftProject.positions.forEach(positionId => positionsPerProject[eventProject].add(positionId))
          }
        }
      }

      // Add all positions of project when filtering by that project
      if (Array.isArray(filter.projects) || Array.isArray(filter.reportsTo) || Array.isArray(filter.projectCodes)) {
        let filteredProjects: string[] = []

        if (filter?.projects?.length || filter?.projectCodes?.length) {
          const projectsIdsFromProjectsCodesFilter = filter.projectCodes?.map(projectCode => projectsByCode[projectCode]._id) || []
          const projectsIdsFromProjectsFilter = filter.projects || []
          filteredProjects = uniq(projectsIdsFromProjectsFilter.concat(projectsIdsFromProjectsCodesFilter))
        } else {
          filteredProjects = projects
            .filter(
              ({ projectOwners = [], status }) =>
                // Find projects that contain at least one of the project owners
                intersection(
                  projectOwners.map(({ _id }) => _id),
                  filter.reportsTo || []
                ).length && !['CANCELLED', 'COMPLETED', 'ARCHIVED'].includes(status)
            )
            .map(({ _id }) => _id)
        }

        for (const projectId of filteredProjects) {
          if (!positionsPerProject[projectId]) positionsPerProject[projectId] = new Set()
          const positions = projectsById[projectId]?.positions || []
          positions.forEach(positionId => positionsPerProject[projectId].add(positionId))
        }
      }

      // Create the positions in the structure for each project
      for (const projectId in positionsPerProject) {
        const { _id, name, code } = projectsById[projectId] || {}
        const projectPosition =
          projectId !== 'noProject'
            ? {
                _id,
                name,
                code,
                resourceType: 'project'
              }
            : { _id: 'noProject', name: t('manager.schedule.NO_PROJECT'), resourceType: 'project' }

        for (const positionId of getRelatedPositions(Array.from(positionsPerProject[projectId]), { children: false })) {
          const position = positionsById[positionId]
          if (!position) continue
          if (!filteredPositionIds.includes(positionId)) continue
          positionResources.push({
            ...position,
            parentId: position.parentId ? `${position.parentId}:${projectId}` : projectId,
            _id: `${positionId}:${projectId}`
          })
        }

        positionResources.push(projectPosition)
      }

      positionResources = sortBy(positionResources, 'name')
      break
    }
    case 'teams': {
      const teamPositions: HierarchisedPosition[] = []
      const visibleTeams: string[] = []
      const loadedTeams = {}

      // Get the list of teams and positions for those teams that
      // are defined by the list of loaded events
      for (const event of events) {
        let eventTeam = event.team
        const { positionId } = event
        if (!eventTeam) eventTeam = 'noTeam'

        visibleTeams.push(eventTeam)

        if (!loadedTeams[eventTeam]) loadedTeams[eventTeam] = { positions: new Set() }
        loadedTeams[eventTeam].positions.add(positionId)
      }

      // If filtering by team or reportsTo, always show that team
      if (filter.teams?.length) {
        visibleTeams.push(...filter.teams)
      } else if (filter.reportsTo?.length) {
        visibleTeams.push(...teams.filter(({ reportsTo }) => intersection(reportsTo, filter.reportsTo).length).map(({ _id }) => _id))
      }

      const sortedVisibleTeams = sortBy(Array.from(uniq(visibleTeams)), teamId => teamsById[teamId]?.name || -Infinity)

      for (const teamId of sortedVisibleTeams) {
        const team = teamsById[teamId] || { _id: 'noTeam', name: t('manager.schedule.NO_TEAM'), positions: [] }

        // Add the top level hiearchy for this team
        teamPositions.push({ _id: team._id, name: team.name, resourceType: 'team' })

        // Add all positions and their parents for this team
        for (const positionId of getRelatedPositions([...(team.positions || []), ...(loadedTeams[teamId]?.positions || [])], { children: false })) {
          const position = positionsById[positionId]
          if (!position) continue
          teamPositions.push({
            ...position,
            _id: `${positionId}:${teamId}`,
            name: position.name,
            parentId: position.parentId ? `${position.parentId}:${teamId}` : teamId
          })
        }
      }

      positionResources = teamPositions
      break
    }
    case 'users': {
      const sortedUsers = sortBy(users, eventSortField)
      for (const user of sortedUsers) {
        positionResources.push({
          name: user.fullName,
          picture: user.picture?.url ?? null,
          firstName: user.firstName,
          lastName: user.lastName,
          _id: user._id,
          resourceType: 'user'
        })
      }
      break
    }
    default:
      positionResources = filteredPositions
      break
  }

  const hierarchy = hierarchise(
    uniqBy(
      positionResources.map(({ _id, name, index, parentId, nonAssignable, resourceType = 'position', code, picture, firstName, lastName }) => ({
        _id,
        id: _id,
        name,
        picture,
        firstName,
        lastName,
        code,
        index,
        parentId,
        expanded: !collapsedResources[_id],
        nonAssignable,
        ...(nonAssignable || ['project', 'team'].includes(resourceType ?? '') ? { eventHeight: resourceType === 'project' ? 46 : 34, marginBottom: 0 } : {}),
        resourceType
      })),
      'id'
    )
  )

  return hierarchy
}

export const createResourceId = ({
  positionId,
  projects = [],
  team = '',
  employeeId = ''
}: {
  positionId: string
  projects: string[]
  team: string
  employeeId: string
}): string => {
  const {
    settings: { currentScheduleHierarchy }
  } = store.getState() as MStore.RootState

  switch (currentScheduleHierarchy) {
    case 'projects':
      return typeof projects[0] === 'string' ? `${positionId}:${projects[0]}` : `${positionId}:noProject`
    case 'teams':
      return team ? `${positionId}:${team}` : `${positionId}:noTeam`
    case 'users':
      return employeeId || ''
    default:
      return positionId
  }
}
export const readResourceId = (resourceId: string): { employeeId?: string; positionId?: string; teamId?: string; projectId?: string; resourceId?: string } => {
  const {
    settings: { currentScheduleHierarchy }
  } = store.getState() as MStore.RootState

  const splitResourceId = resourceId.split(':')

  switch (currentScheduleHierarchy) {
    case 'projects': {
      const positionId = splitResourceId[0]
      const projectId = positionId === 'noProject' ? undefined : splitResourceId[1]
      return { positionId, projectId }
    }
    case 'teams': {
      const positionId = splitResourceId[0]
      const teamId = positionId === 'noProject' ? undefined : splitResourceId[1]
      return { positionId, teamId }
    }
    case 'users': {
      return { employeeId: resourceId }
    }
    default:
      return { positionId: resourceId }
  }
}

export const getProjectId = (resourceId: string): string | null => {
  const splitResourceId = resourceId.split(':')[1]
  const projectId = !splitResourceId || splitResourceId === 'noProject' ? null : splitResourceId

  return projectId
}

// Transforms a flat structure to a hierachical tree
export const hierarchise = (structure: HierarchisedPosition[]): HierarchisedPosition[] => {
  // Handle empty structure
  if (!structure.length) return []

  return sortBy(structure, ['index', 'parentId']).reduce((acc, position) => {
    const positionRef = (acc[position._id] = acc[position._id] || { children: [] })
    const parentRef = (acc[position.parentId || '-'] = acc[position.parentId || '-'] || { children: [] })

    // Map the child to the main acc
    Object.keys(position).map(key => (positionRef[key] = position[key]))
    if (position.parentId) positionRef.parent = parentRef

    // Map the parent to the main acc
    parentRef.children.push(positionRef)

    return acc
  }, {})['-'].children
}

// Transforms a hierachical tree to a flat structure
export const dehierarchise = (hierarchy: Array<HierarchisedPosition>): Array<HierarchisedPosition> => {
  const flat: HierarchisedPosition[] = []
  for (const position of hierarchy) {
    flat.push(position, ...dehierarchise(position.children || []))
  }
  return flat.map(position => {
    position = { ...position }
    delete position.children
    delete position.parent
    return position
  })
}

/**
 * Expands a list of positions to all related positions
 * @param {*} positions a list of positions to expand
 * @returns
 */
export const getRelatedPositions = (positions: string[], { parents = true, children = true } = {}): Array<MData.Position['_id']> => {
  const {
    resources: { positionsById, positions: allPositions }
  } = store.getState() as MStore.RootState

  // Recursive function that finds all parents of a particular position
  const filteredStructure = new Set<MData.Position['_id']>(positions)

  uniq(positions).forEach(positionId => {
    if (children) findPositionChildren(positionId)
    if (parents) findPositionAndParents(positionId)
  })

  return Array.from(filteredStructure)

  // Function to return all children of a position
  function findPositionChildren(positionId: string) {
    const children = allPositions.filter(({ parentId }) => parentId === positionId)
    for (const child of children) {
      filteredStructure.add(child._id)
      findPositionChildren(child._id)
    }
  }

  // Function to return parent lineage of a position
  function findPositionAndParents(positionId: string) {
    const position = positionsById[positionId]
    if (!position) return
    filteredStructure.add(position._id)
    if (position.parentId) findPositionAndParents(position.parentId)
  }
}

export const filterStructure = ({
  positionsToFilter = [],
  departments = [],
  projectIds = [],
  projectCodes = [],
  teams = [],
  reportsTo,
  vacationTypeId = [],
  showAllPositions,
  events = []
}: {
  positionsToFilter: string[]
  departments: string[]
  projectIds: string[]
  projectCodes: string[]
  teams: string[]
  reportsTo: boolean
  vacationTypeId: string[]
  showAllPositions: boolean
  events: MData.Event[]
}): MData.Position[] => {
  const {
    resources: { positions, projectsById, teamsById, projects }
  } = store.getState() as MStore.RootState

  // Display all the positions
  if (showAllPositions) return positions

  // Filter the positions by departments to show only these events
  // If filtering by position as well as department, only show the selected positions.
  if (departments.length > 0 && !positionsToFilter.length) {
    positionsToFilter.push(
      ...filter(positions, position => {
        return intersection(position.departments, departments).length > 0
      }).map(({ _id }) => _id)
    )
  }

  // Filter the positions by projects or projectCodes to show only these events
  // If filtering by position as well as projects or projectCodes, only show the selected positions.
  if ((projectIds.length > 0 || projectCodes.length > 0) && !positionsToFilter.length) {
    const projectIdsToFilter: string[] = []

    if (projectIds.length > 0) {
      projectIdsToFilter.push(...projectIds)
    }

    if (projectCodes.length > 0) {
      const projectsByCode = hashTable(projects, 'code')
      projectIdsToFilter.push(...projectCodes.map(code => projectsByCode[code]?._id))
    }

    const positionsForProjects = uniq(flatten(projectIdsToFilter.map(projectId => projectsById[projectId]?.positions || [])))
    positionsToFilter.push(...positionsForProjects)
  }

  // Filter the positions by teams to show only these events
  // If filtering by position as well as teams, only show the selected positions.
  if (teams.length > 0 && ((!positionsToFilter.length && !reportsTo) || reportsTo)) {
    const positionsForTeams = uniq(flatten(teams.map(teamId => teamsById[teamId]?.positions || [])))
    positionsToFilter.push(...positionsForTeams)
  }

  // Make sure events part of the project/team but with a position not part of it are still visible
  if (positionsToFilter.length) {
    const eventsPositions = events.map(event => event.positionId)
    positionsToFilter = uniq([...positionsToFilter, ...eventsPositions])
  }

  // Remove all positions if filtering by vacation
  if (vacationTypeId.length && !departments.length && !positionsToFilter.length) return []

  if (positionsToFilter.length) {
    const filteredStructure = getRelatedPositions(positionsToFilter)
    return positions.filter(({ _id }) => filteredStructure.includes(_id))
  } else {
    return positions
  }
}

export const getAssignablePositions = (
  { forMultiSelect = false, filterByIds = [], projectId }: { forMultiSelect: boolean; filterByIds?: string[]; projectId?: string } = {
    forMultiSelect: false,
    filterByIds: []
  }
): { value: string; label: string }[] | MData.Position[] => {
  const {
    resources: { positions, projectsById }
  } = store.getState() as MStore.RootState

  const projectPositions = new Set(projectsById[projectId ?? '']?.positions ?? [])

  // Add the isHidden prop and sort in order
  const positionsWithIsHiddenProp = positions.map(position => {
    const isNotInFilter = filterByIds.length > 0 && !filterByIds.includes(position._id)
    const isNotInProjectPositions = !!projectId && projectPositions.size > 0 && !projectPositions.has(position._id)
    const isHidden = position.nonAssignable || isNotInFilter || isNotInProjectPositions

    return { ...position, isHidden }
  })
  const sortedPositions = orderBy(positionsWithIsHiddenProp, ['name'])

  if (!forMultiSelect) return sortedPositions

  return sortedPositions.map(({ _id, name, isHidden }) => ({ value: _id, label: name, isHidden }))
}
